import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Building2, ArrowRight, ArrowLeft, ShieldCheck, Activity, Wrench, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiUrl } from '../config/api';

/**
 * Giris ekrani — uc adimli.
 *
 * 1. 'giris'  : kullanici adi/e-posta + sifre
 * 2. 'firma'  : ayni kullanici adi birden fazla firmada varsa (409
 *               TENANT_AMBIGUOUS) hangi firmaya girilecegi sorulur; secim
 *               X-Tenant basligiyla ayni istegi tekrarlar
 * 3. '2fa'    : iki adimli dogrulama etkinse 6 haneli TOTP kodu
 *
 * login() artik true/false degil, YAPISAL bir sonuc doner (bkz. AuthContext).
 */
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [adim, setAdim] = useState('giris');
  const [firmalar, setFirmalar] = useState([]);
  const [pendingToken, setPendingToken] = useState('');
  const [kod, setKod] = useState('');

  const { login, verify2fa, user } = useAuth();
  const navigate = useNavigate();
  const kodRef = useRef(null);
  // 6. hane girilince otomatik gonderiyoruz; ayni kodun iki kez gonderilmesini
  // engellemek icin son denenen kodu tutuyoruz.
  const sonDenenenKod = useRef('');

  const ilkAdimaDon = () => {
    setAdim('giris');
    setFirmalar([]);
    setPendingToken('');
    setKod('');
    sonDenenenKod.current = '';
  };

  /** login() sonucunu adimlara dagitir */
  const sonucuIsle = (sonuc) => {
    if (sonuc?.ok) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (Array.isArray(sonuc?.tenantSecimi) && sonuc.tenantSecimi.length > 0) {
      setFirmalar(sonuc.tenantSecimi);
      setAdim('firma');
      return;
    }
    if (sonuc?.ikinciAdim === '2fa' && sonuc.pendingToken) {
      setPendingToken(sonuc.pendingToken);
      setKod('');
      sonDenenenKod.current = '';
      setAdim('2fa');
      return;
    }
    // Hata mesaji AuthContext icinde zaten toast'landi; burada tekrarlamiyoruz.
  };

  const hataLogla = async (err) => {
    try {
      await fetch(apiUrl('/api/log-error'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: 'Login error: ' + err.message,
          deviceInfo: {
            type: 'login_exception', username,
            userAgent: navigator.userAgent, platform: navigator.platform,
            timestamp: new Date().toISOString(),
            isAndroid: typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()
          }
        })
      });
    } catch (logErr) { /* sessiz */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Kullanıcı adı ve şifre zorunlu!');
      return;
    }
    setLoading(true);
    try {
      sonucuIsle(await login(username, password));
    } catch (err) {
      const errorMessage = err.message || 'Beklenmeyen bir hata oluştu!';
      toast.error(errorMessage.split('\n')[0], { duration: 6000, style: { maxWidth: '500px', whiteSpace: 'pre-line' } });
      await hataLogla(err);
    } finally {
      setLoading(false);
    }
  };

  /** Firma secildi: ayni kimlik bilgileriyle, bu kez slug belirterek dene */
  const firmaSec = async (slug) => {
    setLoading(true);
    try {
      sonucuIsle(await login(username, password, { tenantSlug: slug }));
    } catch (err) {
      toast.error(err.message || 'Giriş yapılamadı');
      await hataLogla(err);
    } finally {
      setLoading(false);
    }
  };

  const kodDogrula = async (denenecekKod) => {
    const temiz = String(denenecekKod || kod).replace(/\D/g, '');
    if (temiz.length !== 6) {
      toast.error('Doğrulama kodu 6 haneli olmalı');
      return;
    }
    sonDenenenKod.current = temiz;
    setLoading(true);
    try {
      const sonuc = await verify2fa(pendingToken, temiz);
      if (sonuc?.ok) {
        navigate('/dashboard', { replace: true });
      } else if (sonuc?.sureDoldu) {
        // Pending token 5 dk omurlu; suresi dolduysa bastan giris gerekiyor.
        ilkAdimaDon();
      } else {
        setKod('');
        sonDenenenKod.current = '';
        kodRef.current?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  // 2FA adimina gecince kod alanina odaklan
  useEffect(() => {
    if (adim === '2fa') kodRef.current?.focus();
  }, [adim]);

  // 6 hane tamamlaninca otomatik gonder: kullanicinin ayrica butona basmasi
  // gereksiz bir adim.
  useEffect(() => {
    if (adim === '2fa' && kod.length === 6 && !loading && sonDenenenKod.current !== kod) {
      kodDogrula(kod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kod, adim, loading]);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const features = [
    { icon: Wrench, title: 'Akıllı iş emirleri', text: 'Atama, takip ve saha onayı tek akışta.' },
    { icon: Activity, title: 'Kestirimci bakım', text: 'Arıza oluşmadan AI destekli erken uyarı.' },
    { icon: ShieldCheck, title: 'Denetime hazır', text: 'TS EN 81-20 uyumlu kayıt ve raporlama.' }
  ];

  const yukleniyorSimgesi = (
    <span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: 'var(--bg-app)' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .login-brand { display: none; }
        @media (min-width: 960px) { .login-brand { display: flex; } }
        .login-input { width: 100%; height: 50px; padding: 0 16px; border: 1.5px solid var(--border-strong);
          border-radius: 11px; font-size: 15px; background: var(--surface); color: var(--text);
          outline: none; transition: border-color .18s ease, box-shadow .18s ease; }
        .login-input:focus { border-color: var(--primary); box-shadow: 0 0 0 4px var(--primary-ring); }
        .login-input::placeholder { color: var(--text-faint); }
        .login-firma { width: 100%; display: flex; align-items: center; gap: 12px; text-align: left;
          padding: 14px 16px; border: 1.5px solid var(--border-strong); border-radius: 11px;
          background: var(--surface); color: var(--text); cursor: pointer; margin-bottom: 10px;
          transition: border-color .18s ease, box-shadow .18s ease; }
        .login-firma:hover:not(:disabled) { border-color: var(--primary); box-shadow: 0 0 0 4px var(--primary-ring); }
        .login-firma:disabled { opacity: .6; cursor: not-allowed; }
        .login-link { font-size: 13.5px; font-weight: 600; color: var(--primary); text-decoration: none; }
        .login-link:hover { text-decoration: underline; }
      `}</style>

      {/* ===== Brand panel ===== */}
      <div className="login-brand" style={{
        width: '46%', maxWidth: 620, position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(155deg, #1B2349 0%, #0E1525 55%, #0B1120 100%)',
        flexDirection: 'column', justifyContent: 'space-between', padding: '48px 52px', color: '#fff'
      }}>
        <div style={{ position: 'absolute', top: -120, right: -120, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.35), transparent 65%)' }} />
        <div style={{ position: 'absolute', bottom: -140, left: -100, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,158,11,0.16), transparent 65%)' }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 13 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: 'linear-gradient(140deg, #6366F1, #4338CA)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 24px -6px rgba(79,70,229,0.6)' }}>
            <Building2 style={{ width: 25, height: 25, color: '#fff' }} strokeWidth={2.4} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em' }}>Maintenance<span style={{ color: '#818CF8' }}>360</span></div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', color: '#6B7693', textTransform: 'uppercase' }}>Asansör Bakım Platformu</div>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <h1 style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.18, color: '#fff', letterSpacing: '-0.03em', margin: 0 }}>
            Sahadaki her asansör,<br />tek panelde kontrol altında.
          </h1>
          <p style={{ fontSize: 15.5, color: '#AEB6CC', marginTop: 16, lineHeight: 1.6, maxWidth: 440 }}>
            Bakım, arıza, montaj ve denetim süreçlerini uçtan uca yöneten profesyonel CMMS çözümü.
          </p>
          <div style={{ marginTop: 34, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {features.map((f) => (
              <div key={f.title} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(148,163,209,0.10)', border: '1px solid rgba(148,163,209,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <f.icon style={{ width: 20, height: 20, color: '#818CF8' }} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{f.title}</div>
                  <div style={{ fontSize: 13.5, color: '#8A93A8', marginTop: 2 }}>{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 22, fontSize: 12.5, color: '#6B7693', fontWeight: 600 }}>
          <span>TS EN 81-20 / 50</span>
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#3A4560' }} />
          <span>KVKK uyumlu</span>
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#3A4560' }} />
          <span>© {new Date().getFullYear()} Emsentech</span>
        </div>
      </div>

      {/* ===== Form panel ===== */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ width: '100%', maxWidth: 396 }}>
          {/* Mobile brand */}
          <div className="login-brand" style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }} />
          <div style={{ textAlign: 'center', marginBottom: 30 }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: 'linear-gradient(140deg, #6366F1, #4338CA)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-primary)', marginBottom: 16 }}>
              {adim === '2fa'
                ? <KeyRound style={{ width: 28, height: 28, color: '#fff' }} strokeWidth={2.3} />
                : <Building2 style={{ width: 28, height: 28, color: '#fff' }} strokeWidth={2.3} />}
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-0.02em', margin: 0 }}>
              {adim === 'giris' && 'Tekrar hoş geldiniz'}
              {adim === 'firma' && 'Hangi firmaya giriyorsunuz?'}
              {adim === '2fa' && 'İki adımlı doğrulama'}
            </h2>
            <p style={{ fontSize: 14.5, color: 'var(--text-muted)', marginTop: 7 }}>
              {adim === 'giris' && 'Hesabınıza giriş yaparak devam edin'}
              {adim === 'firma' && 'Bu kullanıcı adı birden fazla firmada kayıtlı'}
              {adim === '2fa' && 'Doğrulama uygulamanızdaki 6 haneli kodu girin'}
            </p>
          </div>

          {/* ---------- Adım 1: kimlik bilgileri ---------- */}
          {adim === 'giris' && (
            <>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 18 }}>
                  <label htmlFor="username" className="ds-field-label">Kullanıcı Adı veya E-posta</label>
                  <input id="username" name="username" type="text" required className="login-input"
                    autoComplete="username"
                    placeholder="Kullanıcı adınız veya e-postanız" value={username}
                    onChange={(e) => setUsername(e.target.value)} disabled={loading} />
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label htmlFor="password" className="ds-field-label">Şifre</label>
                  <div style={{ position: 'relative' }}>
                    <input id="password" name="password" type={showPassword ? 'text' : 'password'} required
                      autoComplete="current-password" className="login-input" style={{ paddingRight: 50 }}
                      placeholder="Şifreniz" value={password}
                      onChange={(e) => setPassword(e.target.value)} disabled={loading} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={loading}
                      aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer', color: 'var(--text-faint)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {showPassword ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
                  <Link to="/sifre-sifirla" className="login-link">Şifremi unuttum</Link>
                </div>

                <button type="submit" disabled={loading} className="btn btn-primary ds-btn-lg" style={{ width: '100%' }}>
                  {loading ? (
                    <>
                      {yukleniyorSimgesi}
                      Giriş yapılıyor…
                    </>
                  ) : (
                    <>Giriş Yap <ArrowRight style={{ width: 18, height: 18 }} /></>
                  )}
                </button>
              </form>

              <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13.5, color: 'var(--text-muted)' }}>
                Henüz hesabınız yok mu?{' '}
                <Link to="/kayit" className="login-link">Firmanız için hesap oluşturun</Link>
              </div>
            </>
          )}

          {/* ---------- Adım 2: firma seçimi ---------- */}
          {adim === 'firma' && (
            <div role="radiogroup" aria-label="Giriş yapılacak firma">
              {firmalar.map((f) => (
                <button
                  key={f.slug}
                  type="button"
                  role="radio"
                  aria-checked="false"
                  className="login-firma"
                  disabled={loading}
                  onClick={() => firmaSec(f.slug)}
                >
                  <span style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--primary-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Building2 style={{ width: 19, height: 19, color: 'var(--primary)' }} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>{f.name}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-faint)' }}>{f.slug}</span>
                  </span>
                  <ArrowRight style={{ width: 17, height: 17, color: 'var(--text-faint)', flexShrink: 0 }} />
                </button>
              ))}

              {loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-muted)', marginTop: 12 }}>
                  <span style={{ width: 15, height: 15, border: '2px solid var(--border-strong)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  Giriş yapılıyor…
                </div>
              )}

              <button type="button" onClick={ilkAdimaDon} disabled={loading}
                style={{ width: '100%', marginTop: 14, padding: 10, background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <ArrowLeft style={{ width: 15, height: 15 }} /> Geri dön
              </button>
            </div>
          )}

          {/* ---------- Adım 3: 2FA ---------- */}
          {adim === '2fa' && (
            <form onSubmit={(e) => { e.preventDefault(); kodDogrula(kod); }}>
              <div style={{ marginBottom: 20 }}>
                <label htmlFor="totp-kod" className="ds-field-label">Doğrulama Kodu</label>
                <input
                  id="totp-kod"
                  ref={kodRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="login-input"
                  style={{ textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: '0.4em', paddingRight: 0 }}
                  placeholder="000000"
                  value={kod}
                  disabled={loading}
                  onChange={(e) => setKod(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 8, marginBottom: 0 }}>
                  Kod 6 hane tamamlandığında otomatik gönderilir.
                </p>
              </div>

              <button type="submit" disabled={loading || kod.length !== 6} className="btn btn-primary ds-btn-lg" style={{ width: '100%' }}>
                {loading ? (
                  <>
                    {yukleniyorSimgesi}
                    Doğrulanıyor…
                  </>
                ) : (
                  <>Doğrula <ArrowRight style={{ width: 18, height: 18 }} /></>
                )}
              </button>

              <button type="button" onClick={ilkAdimaDon} disabled={loading}
                style={{ width: '100%', marginTop: 12, padding: 10, background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <ArrowLeft style={{ width: 15, height: 15 }} /> Geri dön
              </button>
            </form>
          )}

          <div style={{ marginTop: 22, padding: '13px 16px', background: 'var(--primary-soft)', border: '1px solid var(--brand-100)', borderRadius: 11, display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldCheck style={{ width: 17, height: 17, color: 'var(--primary)', flexShrink: 0 }} />
            <p style={{ fontSize: 13, color: 'var(--primary-active)', margin: 0 }}>
              Yetkili kullanıcı bilgileriniz ile güvenli giriş yapabilirsiniz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
